import type { Quality } from '@live-stream/shared'
import type { ContentHint } from './encoding.js'
import type { ConnectionState, TrackKind } from './transport.js'

/**
 * What the SFU transport asks for, as plain numbers and names — the pure
 * part, tested without the SDK. sfu.ts turns these into LiveKit objects.
 */

/**
 * The plan's three spatial layers (Phase 7). The SFU forwards to each
 * subscriber the one their tile size and downlink can use: a 160 px
 * thumbnail gets 'q', the speaker gets 'f'. With dynacast, a layer nobody
 * is subscribed to is not encoded at all.
 */
export interface LayerSpec {
  rid: 'q' | 'h' | 'f'
  width: number
  height: number
  maxBitrate: number
  maxFramerate: number
}

export const CAMERA_LAYERS: readonly LayerSpec[] = [
  { rid: 'q', width: 320, height: 180, maxBitrate: 150_000, maxFramerate: 15 },
  { rid: 'h', width: 640, height: 360, maxBitrate: 500_000, maxFramerate: 30 },
  { rid: 'f', width: 1280, height: 720, maxBitrate: 1_700_000, maxFramerate: 30 },
]

/** The top layer, which is what the SFU reports as the send profile. */
export const CAMERA_TOP = CAMERA_LAYERS[CAMERA_LAYERS.length - 1] as LayerSpec

/** A camera whose long side is at least this is a 1080p one: it gets the taller ladder below. */
export const FULL_HD_LONG_SIDE = 1600

/** The layers that depend on how big the camera is: the middle one, and the top one's bitrate. */
export interface CameraLadder {
  mid: LayerSpec
  /** The top layer's cap, VP8 (three streams) and VP9 (one stream with its layers inside). */
  topBitrate: number
  topVp9Bitrate: number
}

/**
 * What to send for a camera whose long side is `longSide` pixels. A 720p camera has the ladder
 * above, and its limits matter: the SFU gives a viewer the best layer whose bitrate their
 * connection can take, so a top layer that costs more than a phone on mobile data can carry
 * leaves that phone on the middle one (they were raised once, to 2.5 and 1.7 Mbps, and a phone
 * with a 460 ms link was put on 360p; they are back at 1.7 and 1.1). A 1080p camera adds a 540p
 * middle step (360p to 1080p is too long a jump for a phone's screen, which is over a thousand
 * pixels across, and a phone that cannot take the top is then given 540p, not 360p) and a top
 * layer with the bits to show 1080p: sent only to those who ask for it, the SFU pausing a layer
 * nobody wants.
 */
export function cameraLadderFor(longSide: number | undefined): CameraLadder {
  if (longSide !== undefined && longSide >= FULL_HD_LONG_SIDE) {
    return {
      mid: { rid: 'h', width: 960, height: 540, maxBitrate: 900_000, maxFramerate: 30 },
      topBitrate: 3_000_000,
      topVp9Bitrate: 2_000_000,
    }
  }
  return {
    mid: CAMERA_LAYERS[1] as LayerSpec,
    topBitrate: CAMERA_TOP.maxBitrate,
    topVp9Bitrate: CAMERA_TOP_VP9_BITRATE,
  }
}

/**
 * What size to open the camera at: 1080p on a computer with the cores to encode it three times
 * over, 720p on a phone (its battery and heat) or a small laptop. The camera gives what it can: a
 * 720p webcam asked for 1080p gives 720p.
 */
export function cameraCaptureFor(env: { cores: number; mobile: boolean }): {
  width: number
  height: number
} {
  return !env.mobile && env.cores >= 6
    ? { width: 1920, height: 1080 }
    : { width: 1280, height: 720 }
}

/**
 * VP9's top bitrate for a camera: about what VP8 needs at two thirds the
 * bits for the same picture. One stream with its layers inside (SVC), so
 * the SFU still sends each viewer the size their tile needs.
 */
export const CAMERA_TOP_VP9_BITRATE = 1_100_000

/**
 * Whether a camera should be sent as VP9: the same picture for roughly a
 * third less data, for every viewer of it. Only where it is safe — a
 * desktop Chromium browser that can encode it; phones keep VP8, whose
 * encoding is lighter on a battery, and Safari and Firefox keep it too
 * (their VP9 encoding with layers is less reliable). Viewers whose browser
 * cannot play VP9 are sent a VP8 copy automatically.
 */
export function cameraCodecFor(env: {
  userAgent: string
  mobile: boolean
  encodesVp9: boolean
}): 'vp9' | 'vp8' {
  if (env.mobile || !env.encodesVp9) return 'vp8'
  const chromium = /\b(Chrome|Chromium|Edg)\//.test(env.userAgent) && !/\bOPR\//.test(env.userAgent)
  return chromium ? 'vp9' : 'vp8'
}

/**
 * Speech: Opus mono at 32 kbps, with DTX so silence costs nothing and RED
 * so a lost packet is recovered from its neighbour. Bad audio ends calls;
 * bad video does not — audio is protected first.
 */
export const MICROPHONE_BITRATE = 32_000
/** A tab's soundtrack is music, not speech: no DTX, more room. */
export const SCREEN_AUDIO_BITRATE = 64_000

export interface ScreenEncoding {
  maxBitrate: number
  maxFramerate: number
  degradationPreference: 'maintain-resolution' | 'maintain-framerate'
}

/**
 * The share is ONE layer: the SFU pauses subscribers who cannot afford it
 * rather than us encoding three copies of a slide. The trade-off follows
 * the content the way the mesh's does.
 */
export function screenEncodingFor(hint: ContentHint): ScreenEncoding {
  return hint === 'motion'
    ? { maxBitrate: 2_500_000, maxFramerate: 30, degradationPreference: 'maintain-framerate' }
    : { maxBitrate: 2_500_000, maxFramerate: 15, degradationPreference: 'maintain-resolution' }
}

/**
 * LiveKit's track sources, by their wire names, and our kinds. The SFU
 * labels every track with its source, so telling a screen from a camera
 * needs no mid map here — the label is part of the publication.
 */
const SOURCE_OF: Record<TrackKind, string> = {
  camera: 'camera',
  microphone: 'microphone',
  screen: 'screen_share',
  'screen-audio': 'screen_share_audio',
}
const KIND_OF: Record<string, TrackKind> = Object.fromEntries(
  Object.entries(SOURCE_OF).map(([kind, source]) => [source, kind as TrackKind]),
)

export const sourceOf = (kind: TrackKind): string => SOURCE_OF[kind]
export const kindOfSource = (source: string): TrackKind | null => KIND_OF[source] ?? null

/** Which spatial layer a tile's quality asks for. 'off' is a paused subscription, not a layer. */
export type LayerChoice = 'low' | 'medium' | 'high' | 'off'
export function layerFor(quality: Quality): LayerChoice {
  switch (quality) {
    case 'off':
      return 'off'
    case 'low':
      return 'low'
    case 'med':
      return 'medium'
    default:
      return 'high'
  }
}

/** LiveKit's room connection states in our vocabulary. */
export function connectionStateOf(state: string): ConnectionState {
  switch (state) {
    case 'connected':
      return 'connected'
    case 'connecting':
      return 'connecting'
    case 'reconnecting':
    case 'signalReconnecting':
      return 'reconnecting'
    case 'disconnected':
      return 'closed'
    default:
      return 'new'
  }
}

/**
 * Which transport a client should use. The URL flag wins, so mesh and SFU
 * can be A/B'd and rolled back from the address bar; otherwise the SFU
 * whenever the server offers one, and the mesh when it does not.
 */
export type TransportMode = 'mesh' | 'sfu'
export function chooseTransport(
  sfuOffered: boolean,
  flag: string | null | undefined,
): TransportMode {
  if (flag === 'mesh' || flag === 'sfu') return flag === 'sfu' && !sfuOffered ? 'mesh' : flag
  return sfuOffered ? 'sfu' : 'mesh'
}
